import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Flex, Breadcrumb, ConfigProvider, theme as antdTheme } from 'antd'
import {
  WHITE_COLOR,
  LIGHT_GRAY_COLOR,
  TRANSPARENT,
  BLACK_COLOR,
} from '../constants'
import { HomeOutlined, LeftOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import ReportTimePicker from './ReportTimePicker'
import { FILTER_TIME_WINDOW } from './utils'

/**
 * Shared layout wrapper for usage-monitor-style report pages.
 *
 * Props:
 *  - pageId: string  — the id applied to the outermost Flex element
 *  - breadcrumbLabel: { id, defaultMessage }  — i18n descriptor for the second breadcrumb item
 *  - breadcrumbSubLabel: { id, defaultMessage }  — i18n descriptor for the third breadcrumb item
 *  - onFilterChange: (start, end) => void
 *  - onSync: () => void
 *  - lastUpdatedTime: Date
 *  - children: React.ReactNode  — the widget content rendered inside the body
 */
const ReportPageLayout = ({
  pageId,
  breadcrumbLabel,
  breadcrumbSubLabel,
  onFilterChange,
  onSync,
  lastUpdatedTime,
  children,
}) => {
  const { defaultAlgorithm } = antdTheme
  const history = useHistory()

  return (
    <Flex vertical id={pageId} style={{ height: '100%' }}>
      <Flex
        vertical={false}
        justify="flex-start"
        gap={10}
        className="reports-breadcrumb-wrapper"
      >
        <LeftOutlined onClick={() => history.push('/reports')} />
        <div>
          <Breadcrumb>
            <Breadcrumb.Item>
              <HomeOutlined />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <FormattedMessage {...breadcrumbLabel} />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <FormattedMessage {...breadcrumbSubLabel} />
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </Flex>
      <Flex className="reports-page-body" vertical={true}>
        <Flex
          vertical={false}
          className="usage-monitor-filter-panel"
          align="center"
          justify="flex-end"
        >
          <Flex
            vertical={false}
            className="usage-monitor-filter-panel-left"
            gap={16}
            justify="flex-end"
          >
            <ReportTimePicker
              filterTimeWindow={FILTER_TIME_WINDOW}
              defaultValue={FILTER_TIME_WINDOW[0].value}
              onFilterChange={onFilterChange}
              showSyncButton
              onSync={onSync}
              lastUpdatedTime={lastUpdatedTime}
            />
          </Flex>
        </Flex>
        <Flex className="usage-monitor-body" vertical gap={12}>
          <ConfigProvider
            theme={{
              algorithm: defaultAlgorithm,
              components: {
                Table: {
                  headerBg: LIGHT_GRAY_COLOR,
                  colorBgContainer: WHITE_COLOR,
                  colorBorderSecondary: LIGHT_GRAY_COLOR,
                  headerColor: BLACK_COLOR,
                  headerSplitColor: TRANSPARENT,
                },
              },
            }}
          >
            {children}
          </ConfigProvider>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default ReportPageLayout
